"use client"

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react"
import { Fragment, useTransition } from "react"
import { useParams, usePathname, useRouter } from "next/navigation"

import { updateLocale } from "@lib/data/locale-actions"

type LocaleCode = "ua" | "ru" | "en"

type Option = {
  code: LocaleCode
  flag: string
  label: string
}

const OPTIONS: Option[] = [
  { code: "ua", flag: "🇺🇦", label: "UA" },
  { code: "ru", flag: "🇷🇺", label: "RU" },
  { code: "en", flag: "🇬🇧", label: "EN" },
]

const SUPPORTED: LocaleCode[] = ["ua", "ru", "en"]

const LanguageSwitcher = () => {
  const params = useParams()
  const pathname = usePathname()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  const raw = (params?.countryCode as string | undefined)?.toLowerCase()
  const currentCode: LocaleCode = (
    raw && (SUPPORTED as string[]).includes(raw) ? raw : "ua"
  ) as LocaleCode

  const current =
    OPTIONS.find((o) => o.code === currentCode) ?? OPTIONS[0]

  const handleChange = (next: Option) => {
    if (next.code === currentCode) return

    const segments = (pathname || "/").split("/")
    if (segments.length > 1 && (SUPPORTED as string[]).includes(segments[1])) {
      segments[1] = next.code
    } else {
      segments.splice(1, 0, next.code)
    }
    const target = segments.join("/") || `/${next.code}`

    startTransition(async () => {
      await updateLocale(next.code)
      router.push(target)
      router.refresh()
    })
  }

  return (
    <Listbox value={current} onChange={handleChange} disabled={isPending}>
      <div className="relative">
        <ListboxButton className="flex items-center gap-1 text-xs text-slate-500 transition hover:text-brand-light disabled:opacity-60 dark:text-slate-500 dark:hover:text-brand-dark">
          <span aria-hidden="true">{current.flag}</span>
          <span>{current.label}</span>
        </ListboxButton>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions className="absolute right-0 top-full z-50 mt-1 w-24 overflow-hidden rounded-md border border-slate-100 bg-white py-1 text-xs shadow-lg focus:outline-none dark:border-white/10 dark:bg-[#0a1020]">
            {OPTIONS.map((opt) => (
              <ListboxOption
                key={opt.code}
                value={opt}
                className={({ focus, selected }) =>
                  `flex cursor-pointer items-center gap-2 px-3 py-1.5 transition ${
                    focus ? "bg-slate-50 dark:bg-white/5" : ""
                  } ${
                    selected
                      ? "text-brand-light dark:text-brand-dark"
                      : "text-slate-600 dark:text-slate-400"
                  }`
                }
              >
                <span aria-hidden="true">{opt.flag}</span>
                <span>{opt.label}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  )
}

export default LanguageSwitcher
